import { cn } from "../lib/utils.js";
import { CheckboxBase } from "../components/checkbox.js";
import { Label } from "../components/label.js";
import { FieldDescription, FieldError, FieldLabel, FieldLabelHint, FieldRoot, FieldValidation } from "../components/field.js";
import { InputBase } from "../components/input-base.js";
import { resolveCompoundFieldErrors } from "./field-errors.js";
import { useResizeObserver } from "../hooks/use-resize-observer.js";
import { useId, useRef, useState } from "react";
import { Eraser, PencilLine } from "@phosphor-icons/react";
import { jsx, jsxs } from "react/jsx-runtime";
import SignatureCanvas from "react-signature-canvas";
//#region src/smart-components/e-signature-input.tsx
const E_SIGNATURE_INPUT_PARTS = ["signature", "agreedToDisclosure"];
const DEFAULT_DISCLOSURE = "By selecting the \"I agree to electronically sign this document\" checkbox, you are signing this document electronically. You agree your electronic signature is the legal equivalent of your manual/handwritten signature on this document.";
const SIGNATURE_CANVAS = {
	fontSize: 32,
	widthPadding: 20,
	renderHeight: 50,
	textX: 10,
	textY: 38,
	displayHeight: "200px"
};
const ESignatureInput = ({ agreement, agreementAffirmationText = "I agree to electronically sign this document", description, disclosure, disabled = false, errors, hint, id, label, onChange, required = false, clearLabel = "Clear signature", canvasAriaLabel = "Signature canvas. Draw your signature here, or type your name in the text field below.", drawPlaceholder = "Draw your signature in this box", textPlaceholder = "Or type your full name here", value = {}, warnings, wrapperClassName, ref }) => {
	const fallbackId = useId();
	const fieldId = id ?? fallbackId;
	const textInputId = `${fieldId}-text`;
	const disclosureId = `${fieldId}-disclosure`;
	const resolvedErrors = resolveCompoundFieldErrors(errors, E_SIGNATURE_INPUT_PARTS);
	const signatureInvalid = Boolean(resolvedErrors.message || resolvedErrors.parts.signature);
	const disclosureInvalid = Boolean(resolvedErrors.parts.agreedToDisclosure);
	const signatureErrorId = `${fieldId}-signature-error`;
	const disclosureErrorId = `${disclosureId}-error`;
	const canvasRef = useRef(null);
	const canvasWrapperRef = useRef(null);
	const backingStoreRef = useRef({
		width: 0,
		height: 0
	});
	const textInputRef = useRef(null);
	const setTextInputRef = (node) => {
		textInputRef.current = node;
		if (typeof ref === "function") ref(node);
		else if (ref) ref.current = node;
	};
	const [showPlaceholder, setShowPlaceholder] = useState(true);
	const existingSignature = showPlaceholder ? value.data : void 0;
	useResizeObserver(canvasWrapperRef, () => {
		const pad = canvasRef.current;
		const canvas = pad?.getCanvas();
		if (!pad || !canvas) return;
		const ratio = Math.max(window.devicePixelRatio || 1, 1);
		const width = canvas.offsetWidth * ratio;
		const height = canvas.offsetHeight * ratio;
		if (!width || !height) return;
		if (backingStoreRef.current.width === width && backingStoreRef.current.height === height) return;
		backingStoreRef.current = {
			width,
			height
		};
		const data = pad.toData();
		canvas.width = width;
		canvas.height = height;
		canvas.getContext("2d")?.scale(ratio, ratio);
		if (!existingSignature) pad.fromData(data);
	});
	const clearTextInput = () => {
		if (textInputRef.current) textInputRef.current.value = "";
	};
	const clearCanvas = () => {
		canvasRef.current?.clear();
		clearTextInput();
		setShowPlaceholder(true);
		onChange?.({
			...value,
			type: void 0,
			data: void 0
		});
	};
	const handleSigningStart = () => {
		setShowPlaceholder(false);
		clearTextInput();
	};
	const handleCanvasEnd = () => {
		if (!canvasRef.current) return;
		onChange?.({
			...value,
			type: "canvas",
			data: canvasRef.current.getTrimmedCanvas().toDataURL()
		});
	};
	const handleTextSign = (e) => {
		const text = e.target.value;
		canvasRef.current?.clear();
		setShowPlaceholder(true);
		if (!text) {
			onChange?.({
				...value,
				type: void 0,
				data: void 0
			});
			return;
		}
		const tempCanvas = document.createElement("canvas");
		const ctx = tempCanvas.getContext("2d");
		if (!ctx) return;
		const font = `italic ${SIGNATURE_CANVAS.fontSize}px serif`;
		ctx.font = font;
		tempCanvas.width = ctx.measureText(text).width + SIGNATURE_CANVAS.widthPadding;
		tempCanvas.height = SIGNATURE_CANVAS.renderHeight;
		ctx.font = font;
		ctx.fillStyle = "#000";
		ctx.fillText(text, SIGNATURE_CANVAS.textX, SIGNATURE_CANVAS.textY);
		onChange?.({
			...value,
			type: "text",
			data: tempCanvas.toDataURL()
		});
	};
	const handleDisclosureChange = (checked) => {
		onChange?.({
			...value,
			agreedToDisclosure: checked === true
		});
	};
	return /* @__PURE__ */ jsxs(FieldRoot, {
		id: fieldId,
		disabled,
		required,
		invalid: resolvedErrors.hasError,
		orientation: "vertical",
		className: wrapperClassName,
		children: [
			label && /* @__PURE__ */ jsxs(FieldLabel, { children: [label, hint && /* @__PURE__ */ jsx(FieldLabelHint, { children: hint })] }),
			description && /* @__PURE__ */ jsx(FieldDescription, { children: description }),
			/* @__PURE__ */ jsxs("div", {
				className: cn("tw:flex tw:flex-col tw:gap-3", disabled && "tw:opacity-50 tw:pointer-events-none"),
				children: [
					/* @__PURE__ */ jsxs("div", {
						ref: canvasWrapperRef,
						className: "tw:relative",
						children: [
							/* @__PURE__ */ jsx("button", {
								type: "button",
								onClick: clearCanvas,
								disabled,
								className: cn("tw:absolute tw:right-3 tw:top-3 tw:z-10 tw:cursor-pointer", "tw:border-none tw:bg-transparent tw:p-0 tw:text-muted-foreground", "tw:hover:text-foreground tw:transition-colors", "tw:disabled:cursor-not-allowed tw:disabled:opacity-50"),
								"aria-label": clearLabel,
								children: /* @__PURE__ */ jsx(Eraser, { className: "tw:size-4" })
							}),
							/* @__PURE__ */ jsx(SignatureCanvas, {
								ref: canvasRef,
								clearOnResize: false,
								onBegin: handleSigningStart,
								onEnd: handleCanvasEnd,
								canvasProps: {
									id: fieldId,
									role: "img",
									"aria-label": canvasAriaLabel,
									"aria-invalid": signatureInvalid,
									"aria-describedby": resolvedErrors.parts.signature ? signatureErrorId : void 0,
									"aria-required": required,
									className: cn("tw:w-full tw:cursor-crosshair tw:rounded-sm tw:border tw:bg-white", signatureInvalid ? "tw:border-destructive" : "tw:border-input"),
									style: { height: SIGNATURE_CANVAS.displayHeight }
								}
							}),
							existingSignature && /* @__PURE__ */ jsx("img", {
								"data-slot": "e-signature-input-existing",
								src: existingSignature,
								alt: "",
								className: "tw:pointer-events-none tw:absolute tw:inset-0 tw:m-auto tw:max-h-full tw:max-w-full tw:object-contain"
							}),
							showPlaceholder && !existingSignature && /* @__PURE__ */ jsxs("div", {
								className: "tw:pointer-events-none tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:gap-1.5 tw:text-sm tw:text-muted-foreground",
								children: [/* @__PURE__ */ jsx(PencilLine, { className: "tw:size-4" }), /* @__PURE__ */ jsx("span", { children: drawPlaceholder })]
							})
						]
					}),
					resolvedErrors.parts.signature && /* @__PURE__ */ jsx(FieldError, {
						id: signatureErrorId,
						children: resolvedErrors.parts.signature
					}),
					/* @__PURE__ */ jsx(InputBase, {
						ref: setTextInputRef,
						id: textInputId,
						type: "text",
						required: false,
						"aria-invalid": false,
						onChange: handleTextSign,
						placeholder: textPlaceholder
					}),
					/* @__PURE__ */ jsxs("div", {
						className: "tw:flex tw:flex-col tw:gap-1",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "tw:flex tw:items-start tw:gap-2",
							children: [/* @__PURE__ */ jsx(CheckboxBase, {
								id: disclosureId,
								className: "tw:mt-0.5 tw:p-0",
								checked: value?.agreedToDisclosure ?? false,
								onCheckedChange: handleDisclosureChange,
								required: false,
								"aria-invalid": disclosureInvalid,
								"aria-describedby": disclosureInvalid ? disclosureErrorId : void 0
							}), /* @__PURE__ */ jsx(Label, {
								htmlFor: disclosureId,
								className: "tw:font-normal tw:text-sm tw:text-muted-foreground tw:cursor-pointer",
								children: agreementAffirmationText
							})]
						}), resolvedErrors.parts.agreedToDisclosure && /* @__PURE__ */ jsx(FieldError, {
							id: disclosureErrorId,
							children: resolvedErrors.parts.agreedToDisclosure
						})]
					}),
					agreement && /* @__PURE__ */ jsxs("p", {
						className: "tw:m-0 tw:rounded-sm tw:border tw:border-input tw:bg-white tw:px-3 tw:py-2 tw:text-xs tw:text-muted-foreground",
						children: ["By signing, you agree to the following: ", agreement]
					}),
					/* @__PURE__ */ jsx("p", {
						className: "tw:m-0 tw:rounded-sm tw:border tw:border-input tw:bg-white tw:px-3 tw:py-2 tw:text-xs tw:text-muted-foreground tw:leading-relaxed",
						children: disclosure || DEFAULT_DISCLOSURE
					})
				]
			}),
			resolvedErrors.message && /* @__PURE__ */ jsx(FieldError, { children: resolvedErrors.message }),
			!resolvedErrors.hasError && warnings && /* @__PURE__ */ jsx(FieldValidation, {
				variant: "warning",
				children: warnings
			})
		]
	});
};
//#endregion
export { ESignatureInput, E_SIGNATURE_INPUT_PARTS };

//# sourceMappingURL=e-signature-input.js.map